import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import { getNoteAttachmentState } from '@collections/attachments';

import { getUsersLoggedIn } from '@collections/users';
import { selectActiveNotesFilters } from '@modules/notes/filters-store/notes-filters.selectors';
import { NotesFiltersInterface } from '@modules/notes/filters-store/notes-filters.service';
import { Store } from '@ngrx/store';
import { AttachmentService } from '@shared';
import { User } from '@shared/models';
import { getVisibleToTeamMembersText, isTruthy } from '@shared/utils';
import { AvatarBackgroundSizes, AvatarShapes } from 'mojo-avatar';
import { getExtensionFromFileName, getFileNameWithoutExtension } from 'mojo-irp-attachments';

import { NoteCategories, PersistedNote, mojoNotesPreArchive } from 'mojo-irp-notes';
import { Observable, Subscription } from 'rxjs';
import { first } from 'rxjs/operators';

@Component({
  selector: 'mojo-notes-item',
  templateUrl: './notes-item.component.html',
  styleUrls: ['./notes-item.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class NotesItemComponent implements OnInit, OnDestroy {
  @Input() note: PersistedNote;
  @Input() author: User;
  @Input() fullpage = true;
  @Input() sideDrawerConfiguration = false;
  @Output() openEdit = new EventEmitter();

  avatarPhotoShape = AvatarShapes.circle;
  avatarBackground = AvatarBackgroundSizes.cover;

  user$: Observable<User> = this.store.select(getUsersLoggedIn).pipe(first(isTruthy));
  activeNotesFilters: NotesFiltersInterface;

  draft = NoteCategories.Draft.toString();
  labelTxt;

  noteAttachmentState$ = this.store.select(getNoteAttachmentState);

  private subscription = new Subscription();

  constructor(private store: Store<{}>, private attachmentService: AttachmentService) {
    this.subscribeToActiveNotesFilters();
  }

  ngOnInit() {
    this.labelTxt = this.note?.isPublic
      ? undefined
      : this.note?.teamMembers?.length > 0
      ? `ME + ${this.note?.teamMembers?.length}`
      : 'ME ONLY';
  }

  subscribeToActiveNotesFilters() {
    this.subscription.add(
      this.store.select(selectActiveNotesFilters).subscribe((notesFilters) => {
        this.activeNotesFilters = notesFilters;
      }),
    );
  }

  deleteNote() {
    this.store.dispatch(mojoNotesPreArchive({ note: this.note }));
  }

  isAuthor(user: User) {
    return !!user && user.id === +this.note.createdById;
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  openEditModal() {
    this.openEdit.emit({
      authorId: this.author?.id,
      author: `${this.author?.firstName} ${this.author?.lastName}`,
      note: this.note,
    });
  }

  getFileNameWithoutExtension(name: string): string {
    return getFileNameWithoutExtension(name);
  }

  getExtension(name: string): string {
    return getExtensionFromFileName(name);
  }

  getUrl(id: number) {
    this.subscription.add(
      this.attachmentService.getUrl(id).subscribe((url) => window.open(url, '_blank')),
    );
  }

  isNoteLoaded(state) {
    return (
      state.noteAttachment.noteId !== this.note?.id ||
      (state.noteAttachment.noteId === this.note?.id && !!state.noteAttachment.loaded)
    );
  }

  protected readonly getVisibleToText = getVisibleToTeamMembersText;
}
